import {Inject, Injectable} from '@angular/core'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {Store} from '@ngrx/store'
import {Observable, of} from 'rxjs'
import { AuthContextStorageService } from '../../../../core/auth/auth-context-storage.service'
import {SubjectControllerService} from '../../../../api/backend'
import {SelectedDepartmentService} from '../../../../core/services/selected-department.service'
import {catchError, filter, map, switchMap, take} from 'rxjs/operators'
import {StoreKeysEnum} from '../../../../store/store-keys'
import {PerfectScrollbarActions} from '../../../../shared/ui/perfect-scrollbar/state/actions-type'
import {LpisUserSubjectSearchActions} from './action-types'
import {ILpisUserSubjectSearchFilterCriteria} from './lpis-user-subject-search.reducer'
import {selectLpisUserSubjectSearchFilterCriteria} from './lpis-user-subject-search.selectors'
import {CommonService} from '../../../../core/services/common.service'

@Injectable()
export class LpisUserSubjectSearchEffects {

    fetchData$: Observable<any>
    filterCriteriaAction$: Observable<any>
    itemCollectionAction$: Observable<any>
    $toggleExceptLpisId: Observable<any>
    $resetSearch: Observable<any>

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(Store) private store: Store,
                @Inject(CommonService) private commonService: CommonService,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
                @Inject(SubjectControllerService) protected subjectControllerService: SubjectControllerService,
                @Inject(SelectedDepartmentService) private selectedDepartmentService: SelectedDepartmentService) {
        // =======================
        // Actions called from PerfectScrollbar
        // reflects dataLoaderAction from origin component
        // =======================
        this.fetchData$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.fetchOriginData),
            filter((action) => action.key === StoreKeysEnum.LpisUserSubjectSearchComponent),
            switchMap((action) =>
                this.fetchData().pipe(
                    map(data => PerfectScrollbarActions.postLoadAction({key: action.key, data})),
                    catchError(() => of(LpisUserSubjectSearchActions.noAction())),
                ),
            ),
        ))
        // reflects filterCriteriaAction from origin component
        this.filterCriteriaAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.originFilterCriteriaAction),
            filter((action) => action.key === StoreKeysEnum.LpisUserSubjectSearchComponent),
            map((action) => LpisUserSubjectSearchActions.updateFilterCriteria({ criteriaFragment: action.fragment }),
            ),
        ))
        // reflects itemCollectionAction from origin component
        this.itemCollectionAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setOriginItemCollection),
            filter((action) => action.key === StoreKeysEnum.LpisUserSubjectSearchComponent),
            map((action) => LpisUserSubjectSearchActions.setItemCollection({ itemCollection: action.collection }),
            ),
        ))

        this.$toggleExceptLpisId = createEffect(() => this.actions$.pipe(
            ofType(LpisUserSubjectSearchActions.toggleExceptLpisId),
            switchMap((action) => {
                return this.prepareToggleOwnershipList(action.lpisId).pipe(
                    map(exceptLpisIdList => {
                        return LpisUserSubjectSearchActions.updateFilterCriteria({criteriaFragment: {exceptLpisIds: exceptLpisIdList}})
                    }),
                )
            }),
        ))

        this.$resetSearch = createEffect(() => this.actions$.pipe(
            ofType(LpisUserSubjectSearchActions.resetSearch),
            switchMap(() => {
                return this.store.select(selectLpisUserSubjectSearchFilterCriteria).pipe(
                    take(1),
                    switchMap(criteria => {
                        const exceptLpisIds = criteria.exceptLpisIds
                        return [
                            LpisUserSubjectSearchActions.resetState(),
                            LpisUserSubjectSearchActions.updateFilterCriteria({criteriaFragment: {exceptLpisIds: exceptLpisIds}})
                        ]
                    })
                )
            })
        ))
    }

    private fetchData(): Observable<any> {
        return this.store.select(selectLpisUserSubjectSearchFilterCriteria).pipe(
            take(1),
            switchMap((filterCriteria: ILpisUserSubjectSearchFilterCriteria) => {
                return this.subjectControllerService.getLpisUsers(
                    this.authContextStorageService.getProjectKey(),
                    this.selectedDepartmentService.getSelectedDepartmentId(),
                    filterCriteria.limit,
                    filterCriteria.offset,
                    filterCriteria.searchText,
                    filterCriteria.exceptLpisIds,
                )
            }),
        )
    }

    private prepareToggleOwnershipList(lpisId: number): Observable<any> {
        return this.store.select(selectLpisUserSubjectSearchFilterCriteria).pipe(
            take(1),
            map((criteria) => {
                let newOwnershipList = [...criteria.exceptLpisIds]
                this.commonService.addOrRemoveValue(newOwnershipList, lpisId)
                return newOwnershipList
            }))
    }
}